import {
	createContext,
	ReactNode,
	useContext,
	useEffect,
	useState,
} from 'react';
import _ from 'lodash';
import { Button, Container, Navbar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import VehicleMassiveUpdateModal from './VehicleMassiveUpdateModal';
import VehicleBulkDeleteButton from './VehicleBulkDeleteButton';
import { VehicleStateById } from './vehicleBulkDelete';

type ContextType = {
	ids: string[];
	setIds: (ids: string[]) => void;
	totalCount: number;
	setTotalCount: (count: number) => void;
	allIds: string[];
	setAllIds: (ids: string[]) => void;
	stateById: VehicleStateById;
	setStateById: (stateById: VehicleStateById) => void;
	openModal: () => void;
	reset: () => void;
	toggleId: (id: string, checked: boolean) => void;
	setActiveFilters: (list: any[]) => void;
	areAllIncluded: (list: string[]) => boolean;
};

export const VehicleMassiveUpdateContext = createContext<ContextType>({
	ids: [],
	setIds: _.noop,
	totalCount: 0,
	setTotalCount: _.noop,
	allIds: [],
	setAllIds: _.noop,
	stateById: {},
	setStateById: _.noop,
	openModal: _.noop,
	reset: _.noop,
	toggleId: _.noop,
	setActiveFilters: _.noop,
	areAllIncluded: _.noop,
});

export const VehicleMassiveUpdateProvider = ({
	children,
}: {
	children: ReactNode;
}) => {
	const [ids, setIds] = useState<string[]>([]);
	const [allIds, setAllIds] = useState<string[]>([]);
	const [stateById, setStateById] = useState<VehicleStateById>({});
	const [totalCount, setTotalCount] = useState(0);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [activeFilters, setActiveFilters] = useState<any[]>([]);

	const { t } = useTranslation();

	const reset = () => setIds([]);
	const openModal = () => setIsModalOpen(true);
	const closeModal = () => setIsModalOpen(false);

	const toggleId = (id: string, checked: boolean) => {
		const idx = ids.findIndex((v) => v == id);

		if (checked && idx == -1) {
			setIds((prev) => prev.concat([id]));
			return;
		}

		if (!checked) {
			setIds((prev) => prev.filter((v) => v !== id));
		}
	};

	useEffect(() => {
		reset();
	}, [activeFilters.length]);

	const areAllIncluded = (checkIds: string[]) => {
		return checkIds.every((id) => ids.includes(id));
	};

	return (
		<VehicleMassiveUpdateContext.Provider
			value={{
				ids,
				setIds,
				totalCount,
				setTotalCount,
				allIds,
				setAllIds,
				stateById,
				setStateById,
				openModal,
				reset,
				toggleId,
				setActiveFilters,
				areAllIncluded,
			}}
		>
			{children}

			{ids.length > 0 && (
				<Navbar fixed="bottom" bg="dark" className="py-3 px-4">
					<Container
						fluid
						className="d-flex justify-content-between align-items-center"
					>
						<div className="text-white">
							<span>
								{t('vehicles_selected_of_total', {
									selected: ids.length,
									total: totalCount,
								})}
							</span>
							<span
								role="button"
								className="text-white text-decoration-underline ms-3"
								style={{ cursor: 'pointer' }}
								onClick={reset}
							>
								[{t('deselect_all_count', { count: ids?.length || 0 })}]
							</span>
						</div>
						<div className="d-flex gap-3">
							<VehicleBulkDeleteButton />
							<Button variant="light" onClick={openModal}>
								{t('massive_vehicle_update').toUpperCase()}
							</Button>
						</div>
					</Container>
				</Navbar>
			)}

			<VehicleMassiveUpdateModal isOpen={isModalOpen} closeModal={closeModal} />
		</VehicleMassiveUpdateContext.Provider>
	);
};

export const useVehicleMassiveUpdateContext = () =>
	useContext(VehicleMassiveUpdateContext);

export default VehicleMassiveUpdateContext;
